import { mapStateShape, urlBoolean } from "@mountaya/studio";
import { MAX_OPEN_COPILOT_TABS } from "@mountaya/studio/handlers";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { useIdentityStore } from "@mountayaapp/theme";

import { Studio } from "../studio/components/Studio";

/**
 * agentThreadSchema is one persisted chat: its opaque id (tab identity) and the
 * run id of its in-flight agent run (null before a run opens). The studio hosts
 * many concurrent chats over one workspace; persisting the set lets a reload
 * reconnect every in-flight conversation.
 */
const agentThreadSchema = z.object({
  id: z.string().min(1).max(64),
  runId: z.string().max(128).nullable(),
});

/**
 * threadsParam is the persisted chat set, bounded because it is attacker-supplied:
 * the provider mounts one live session — and one open event stream — per entry, so
 * an unbounded list is an unbounded number of streams opened by a crafted link. It
 * is deduplicated by id and capped at the same constant the create gate refuses
 * past, so the URL can never restore a set the product itself would not open.
 *
 * An over-long list is TRUNCATED rather than rejected — failing validateSearch
 * outright would take the whole studio down over one extra entry, a worse denial
 * than the one this closes. A structurally malformed ENTRY still rejects, which is
 * this param's own long-standing contract: a thread missing its runId key is a
 * corrupt persisted set, not a fresh conversation.
 */
const threadsParam = z
  .array(agentThreadSchema)
  .optional()
  .transform((threads) =>
    threads === undefined
      ? undefined
      : [...new Map(threads.map((thread) => [thread.id, thread])).values()].slice(
          0,
          MAX_OPEN_COPILOT_TABS,
        ),
  );

/**
 * studioSearchSchema validates the studio route search by composing two facets.
 * The shared map-state shape carries the display state the studio both
 * initializes from on load and live-writes as the user changes it — the active
 * overlay, terrain, hillshade enrichment, overlay time (minutes-from-now), and
 * the content selection (itinerary_id). The agent
 * facet carries the open chats (each with its run id) and activeThread the one
 * whose conversation the panel shows; the agent surface writes them as chats
 * open, switch, and start runs, and reads them on mount so a reload reconnects
 * every conversation. `creating` opens the itinerary create surface in the
 * sidenav with no itinerary loaded; it lives in the URL (not transient state) so
 * a reload stays on the create surface, and it is cleared the moment the create
 * resolves and ?itinerary_id= takes over. `organization_id` is the sidenav's scope:
 * present, the tree is that organization's itineraries grouped by its collections;
 * absent, it is the caller's personal view. It rides the URL so the scope survives a
 * reload and a shared link opens on the same tree. The import surface mounts purely
 * off the store's open-content slot (a reload closes it), so it carries no URL param:
 * it opens by clearing the detail keys and is dropped the moment the confirm resolves
 * and ?itinerary_id= opens the imported itinerary (or a discard closes the surface).
 * Everything is parsed at the route boundary, so components read typed values, never
 * raw strings.
 */
export const studioSearchSchema = z.object({
  ...mapStateShape,
  activeThread: z.string().optional(),
  creating: urlBoolean,
  // Tolerant, like the rest of this schema: a hand-edited or stale URL naming a non-string
  // scope falls back to the personal one rather than failing validateSearch, which would take
  // the whole studio down over one bad key. It is the ONLY validation of this value — the hook
  // that reads it takes a typed seam and folds rather than parses.
  organization_id: z.string().optional().catch(undefined),
  threads: threadsParam,
});

/**
 * Route is /studio: the in-app studio, nested under _app so it inherits the auth
 * guard + the app shell. It renders the studio map surface, which reads the
 * shared bus + tool context + active itinerary from the StudioProvider mounted
 * in the _app layout (the same provider the sidenav itinerary detail reads). The
 * route validates the full map-state search so it stays the typed source of
 * truth: StudioProvider keys the open itinerary off itinerary_id, and the studio
 * initializes the map's overlay/terrain/hillshade/time from the search on load
 * and reflects later changes back into it.
 */
//
// A scope naming an organization the reader holds no membership in is dropped by the _app guard,
// which settles the memberships, rather than by a guard here: a `beforeLoad` on this route runs
// on every write of the studio's own search, and the studio writes it as the map moves.
export const Route = createFileRoute("/_app/studio")({
  component: StudioRoute,
  validateSearch: studioSearchSchema,
});

function StudioRoute() {
  // Street View follows the reader rather than the scope: a member of an organization whose
  // agreement includes Map Studio keeps it in every scope, their personal space included.
  const holdsStudio = useIdentityStore((state) => state.studioOrganizationIds.length > 0);

  return <Studio holdsStudio={holdsStudio} />;
}
